import { useMemo } from 'react';
import { ChevronDownIcon, SlidersHorizontalIcon } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useCurrentUser } from '@/features/identity';
import {
  TaskFilterRow,
  narrowingSummary,
  type TaskNarrowing,
} from '@/features/tasks';
import { useCanWork } from '@/features/workspaces/canWork';
import type {
  LabelView,
  TaskView,
  UserSummary,
  WorkflowView,
} from '@/lib/api/generated/model';
import {
  HEADER_PILL,
  HEADER_PILL_ACTIVE,
  HEADER_PILL_IDLE,
} from '@/shared/components/pillClasses';

import {
  FIELD_LABEL,
  RANGE_FIELDS,
  RANGE_SPANS,
  SPAN_LABEL,
  activeFilterCount,
  peopleOfTasks,
  personName,
  stateLabels,
  type TaskListState,
  type TaskListView,
} from '../listView';
import { WAITING_ON_SUBTASKS_FILL } from '@/shared/components/stateFills';
import { TaskListDensity } from './TaskListDensity';
import { TaskListViews } from './TaskListViews';

export interface TaskListFiltersProps {
  registry: readonly LabelView[];
  workflows: readonly WorkflowView[];
  /** The tasks the list is drawn from, for the people the filters offer. */
  tasks: readonly TaskView[];
  view: TaskListView;
  onViewChange: (next: TaskListView) => void;
  slashFocusesSearch: boolean;
  /** How many rows the view keeps, and out of how many — the row's count. */
  shown: number;
  total: number;
  /** The narrow window's Filters panel, owned by the page (spec 113 R7). */
  sheetOpen: boolean;
  onSheetOpenChange: (open: boolean) => void;
}

/**
 * The states the menu offers, in its order, with their dots. Their words are
 * `stateLabels`: the board's for the states it shares, plain ones for the
 * rest, and a reader's for a gate (spec 193).
 */
const STATE_OPTIONS: { value: TaskListState; dot: string }[] = [
  { value: 'running', dot: 'bg-board-state-running' },
  { value: 'subtasks', dot: WAITING_ON_SUBTASKS_FILL },
  { value: 'waiting', dot: 'bg-board-state-waiting' },
  { value: 'failed', dot: 'bg-board-state-failed' },
  { value: 'blocked', dot: 'bg-board-state-waiting' },
  { value: 'review', dot: 'bg-board-state-waiting' },
  { value: 'attention', dot: 'bg-board-state-failed' },
  { value: 'to-start', dot: 'bg-board-state-waiting' },
  { value: 'at-rest', dot: 'bg-muted-foreground/25' },
  { value: 'completed', dot: 'bg-board-state-done' },
];

const ANY = '';

const SEARCH_PLACEHOLDER = 'Search title, workflow, step, person…';

/**
 * The Tasks page's filters (spec 112 US1, rearranged by spec 113 US4).
 *
 * Left to right: the built-in views (spec 114), search, labels, state,
 * **assignee**, workflow, More filters,
 * then the count of what they leave. The assignee left More filters because
 * "what is mine" is the question asked most (FR-012); More keeps the rarer
 * three — creator, time, hidden. The count sits at the end of the row it
 * describes rather than on a line of its own (FR-013), and "Clear filters"
 * left for the chip row, which says what it would clear.
 *
 * **Two presentations of one set of controls** (FR-014). At `md` and wider the
 * controls sit in the row, which wraps rather than scrolling sideways — a
 * scrolling row hid More filters off its edge with nothing to say it was there.
 * Below `md`, the search box stays and everything else moves into a Filters
 * panel. The switch is CSS, not a media-query hook, and each filter is defined
 * once and rendered in whichever presentation is in use.
 *
 * The search and labels are the shared `TaskFilterRow`, with its state pills
 * and its own "Clear all" switched off: the board and Overview keep one state
 * at a time (spec 112 FR-007), and reusing the row keeps one search box and one
 * label picker, debounce and `/` included, across all three pages.
 */
export function TaskListFilters({
  registry,
  workflows,
  tasks,
  view,
  onViewChange,
  slashFocusesSearch,
  shown,
  total,
  sheetOpen,
  onSheetOpenChange,
}: TaskListFiltersProps) {
  const me = useCurrentUser().data?.id;
  const canWork = useCanWork();
  const stateOptions = useMemo(() => {
    const label = stateLabels(canWork);
    return STATE_OPTIONS.map((option) => ({
      ...option,
      label: label[option.value],
    }));
  }, [canWork]);

  const shared = useMemo<TaskNarrowing>(
    () => ({ q: view.q, labels: view.labels }),
    [view.q, view.labels],
  );
  const onSharedChange = (next: TaskNarrowing) => {
    onViewChange({ ...view, q: next.q, labels: next.labels });
  };

  // Workflows that hold a task — an empty one could only empty the list.
  const offeredWorkflows = useMemo(
    () =>
      workflows.filter((workflow) =>
        tasks.some((task) => task.workflow_id === workflow.id),
      ),
    [workflows, tasks],
  );

  // People who hold or created a task here; the member themself is offered as
  // "me" instead, so they are not listed twice.
  const people = useMemo(() => peopleOfTasks(tasks, me), [tasks, me]);

  // Everything the panel holds — every filter but the search, which stays out.
  const panelCount = activeFilterCount(view) - (view.q ? 1 : 0);

  // The same filters in the row and in the panel; the panel names the label
  // picker by what it filters, as State and Workflow are named (spec 271).
  // "Label" rather than "Labels": the visible word stays inside the name
  // "Filter by label", so asking for it by voice still finds it.
  const controls = (labelsTrigger?: string) => (
    <>
      <TaskFilterRow
        layout="inline"
        registry={registry}
        narrowing={shared}
        onChange={onSharedChange}
        controls={{
          search: false,
          labels: true,
          state: false,
          clearAll: false,
        }}
        labelsTrigger={labelsTrigger}
      />

      <MultiSelect
        label="Filter by state"
        title="State"
        options={stateOptions}
        selected={view.states}
        onChange={(states) => {
          onViewChange({ ...view, states });
        }}
      />

      <AssigneeFilter
        value={view.assignee}
        people={people.assignees}
        onChange={(assignee) => {
          onViewChange({ ...view, assignee });
        }}
      />

      <MultiSelect
        label="Filter by workflow"
        title="Workflow"
        options={offeredWorkflows.map((workflow) => ({
          value: workflow.key,
          label: workflow.name,
        }))}
        selected={view.workflows}
        onChange={(keys) => {
          onViewChange({ ...view, workflows: keys });
        }}
      />

      <MoreFilters
        view={view}
        creators={people.creators}
        onViewChange={onViewChange}
      />
    </>
  );

  return (
    <div
      data-testid="task-list-filters"
      className="flex flex-wrap items-center gap-2"
    >
      {/* The questions asked most, first (spec 114 FR-001). Every width. */}
      <TaskListViews view={view} onViewChange={onViewChange} />

      <TaskFilterRow
        layout="inline"
        registry={registry}
        narrowing={shared}
        onChange={onSharedChange}
        controls={{
          search: true,
          labels: false,
          state: false,
          clearAll: false,
        }}
        slashFocusesSearch={slashFocusesSearch}
        placeholder={SEARCH_PLACEHOLDER}
      />

      <div
        data-testid="task-list-filters-inline"
        className="hidden md:contents"
      >
        {controls()}
      </div>

      {/* Phone only: beside the search, a 44px icon with the count of
          filters on, not a pill on a line of its own (spec 271). */}
      <button
        type="button"
        aria-label={
          panelCount > 0 ? `Filters (${String(panelCount)})` : 'Filters'
        }
        onClick={() => {
          onSheetOpenChange(true);
        }}
        className={`${HEADER_PILL} ${panelCount > 0 ? HEADER_PILL_ACTIVE : HEADER_PILL_IDLE} min-w-11 justify-center rounded-lg md:hidden`}
      >
        <SlidersHorizontalIcon aria-hidden="true" className="size-4" />
        {panelCount > 0 ? (
          <span className="tabular-nums">{panelCount}</span>
        ) : null}
      </button>

      <Sheet open={sheetOpen} onOpenChange={onSheetOpenChange}>
        <SheetContent side="bottom">
          <SheetHeader>
            <SheetTitle>Filters</SheetTitle>
          </SheetHeader>
          {/* Each filter a full-width row, its chevron at the end; the chips of
              labels in force stay pills under theirs. */}
          <SheetBody className="flex flex-col items-stretch gap-3 pb-6 [&_[aria-haspopup]]:w-full [&_[aria-haspopup]]:justify-start [&_[aria-haspopup]]:rounded-lg [&_[aria-haspopup]>svg:not(:first-child)]:ms-auto">
            {controls('Label')}
          </SheetBody>
        </SheetContent>
      </Sheet>

      {total > 0 ? (
        <p
          aria-live="polite"
          // On a phone it ends the views' line, so the search has its own.
          className="text-muted-foreground ml-auto text-xs whitespace-nowrap tabular-nums max-md:-order-1"
        >
          {narrowingSummary(shown !== total, shown, total)}
        </p>
      ) : null}

      {/* How tall the rows are — last, after the count (spec 114 FR-006). */}
      <TaskListDensity />
    </div>
  );
}

/**
 * Who holds the task — one choice at a time (spec 113 FR-012). Promoted out
 * of More filters, where "Assigned to me" was three clicks deep.
 */
function AssigneeFilter({
  value,
  people,
  onChange,
}: {
  value: string | undefined;
  people: readonly UserSummary[];
  onChange: (next: string | undefined) => void;
}) {
  const person = people.find((p) => p.id === value);
  const text =
    value === undefined
      ? 'Assignee'
      : value === 'me'
        ? 'Assigned to me'
        : value === 'none'
          ? 'Unassigned'
          : person
            ? personName(person)
            : 'Assignee';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Filter by assignee"
        className={`${HEADER_PILL} ${value !== undefined ? HEADER_PILL_ACTIVE : HEADER_PILL_IDLE}`}
      >
        <span className="max-w-48 truncate">{text}</span>
        <ChevronDownIcon aria-hidden="true" className="size-3" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-[min(var(--available-height),28rem)] min-w-52"
      >
        <DropdownMenuRadioGroup
          value={value ?? ANY}
          onValueChange={(next: string) => {
            onChange(next || undefined);
          }}
        >
          <DropdownMenuRadioItem value={ANY}>Anyone</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="me">
            Assigned to me
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="none">Unassigned</DropdownMenuRadioItem>
          {people.map((p) => (
            <DropdownMenuRadioItem key={p.id} value={p.id}>
              {personName(p)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The rarer filters: who created it, when, and whether hidden tasks show. */
function MoreFilters({
  view,
  creators,
  onViewChange,
}: {
  view: TaskListView;
  creators: readonly UserSummary[];
  onViewChange: (next: TaskListView) => void;
}) {
  const count = [view.creator, view.range, view.hidden].filter(Boolean).length;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={
          count > 0 ? `More filters (${String(count)})` : 'More filters'
        }
        className={`${HEADER_PILL} ${count > 0 ? HEADER_PILL_ACTIVE : HEADER_PILL_IDLE}`}
      >
        <SlidersHorizontalIcon aria-hidden="true" className="size-3" />
        More filters
        {count > 0 ? ` (${String(count)})` : null}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-[min(var(--available-height),28rem)] min-w-60"
      >
        <DropdownMenuGroup>
          <DropdownMenuLabel>Created by</DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={view.creator ?? ANY}
            onValueChange={(value: string) => {
              onViewChange({ ...view, creator: value || undefined });
            }}
          >
            <DropdownMenuRadioItem value={ANY}>Anyone</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="me">
              Created by me
            </DropdownMenuRadioItem>
            {creators.map((person) => (
              <DropdownMenuRadioItem key={person.id} value={person.id}>
                {personName(person)}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuGroup>

        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuLabel>Time</DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={view.range?.since ?? ANY}
            onValueChange={(value: string) => {
              const since = RANGE_SPANS.find((span) => span === value);
              onViewChange({
                ...view,
                range: since
                  ? { since, on: view.range?.on ?? 'created' }
                  : undefined,
              });
            }}
          >
            <DropdownMenuRadioItem value={ANY}>Any time</DropdownMenuRadioItem>
            {RANGE_SPANS.map((span) => (
              <DropdownMenuRadioItem key={span} value={span}>
                {SPAN_LABEL[span]}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
          {view.range ? (
            <DropdownMenuRadioGroup
              aria-label="Time applies to"
              value={view.range.on}
              onValueChange={(value: string) => {
                const on = RANGE_FIELDS.find((field) => field === value);
                if (on && view.range) {
                  onViewChange({ ...view, range: { ...view.range, on } });
                }
              }}
            >
              {RANGE_FIELDS.map((field) => (
                <DropdownMenuRadioItem key={field} value={field} inset>
                  {FIELD_LABEL[field]}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          ) : null}
        </DropdownMenuGroup>

        <DropdownMenuSeparator />
        <DropdownMenuCheckboxItem
          checked={view.hidden}
          onCheckedChange={(checked: boolean) => {
            onViewChange({ ...view, hidden: checked });
          }}
        >
          Show hidden tasks
        </DropdownMenuCheckboxItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * A pill opening a list of options, several of which can be on at once — the
 * values OR together (spec 112 FR-001, FR-002). It stays open while options
 * are toggled, so choosing three states is three clicks, not three trips.
 */
function MultiSelect<Value extends string>({
  label,
  title,
  options,
  selected,
  onChange,
}: {
  label: string;
  title: string;
  options: { value: Value; label: string; dot?: string }[];
  selected: readonly Value[];
  onChange: (next: Value[]) => void;
}) {
  const names = options
    .filter((option) => selected.includes(option.value))
    .map((option) => option.label);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={label}
        className={`${HEADER_PILL} ${names.length > 0 ? HEADER_PILL_ACTIVE : HEADER_PILL_IDLE}`}
      >
        <span className="max-w-48 truncate">
          {names.length === 0
            ? title
            : names.length === 1
              ? names[0]
              : `${title}: ${String(names.length)}`}
        </span>
        <ChevronDownIcon aria-hidden="true" className="size-3" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-52">
        {options.map((option) => (
          <DropdownMenuCheckboxItem
            key={option.value}
            checked={selected.includes(option.value)}
            closeOnClick={false}
            onCheckedChange={(checked: boolean) => {
              onChange(
                checked
                  ? [...selected, option.value]
                  : selected.filter((value) => value !== option.value),
              );
            }}
          >
            {option.dot ? (
              <span
                aria-hidden="true"
                className={`size-2 shrink-0 rounded-full ${option.dot}`}
              />
            ) : null}
            {option.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
